import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { priceModes, serviceMethods } from '../data/marketplace';
import { LOCATION_TREE, splitLocation } from '../data/locations';
import useServiceCategories from '../data/useServiceCategories';
import './Marketplace.css';

export default function PublishService() {
  const { language, localizeCategory, localizeMethod, translatePhrase } = useLanguage();
  const { user } = useAuth();
  const serviceCategories = useServiceCategories();
  const text = language === 'zh' ? {
    kicker: '发布服务',
    title: '创建可下单的服务资料',
    subtitle: '把服务内容、价格方式、服务地区和交付节奏写清楚，客户就能直接浏览、咨询和下单。',
    success: '提交成功，等待审核',
    editSuccess: '服务已更新，等待重新审核',
    successText: '高风险类目会进入人工或资质审核，普通服务审核通过后将在技能服务页展示。',
    back: '返回技能服务',
    serviceTitle: '服务标题 *',
    titlePlaceholder: '如：家庭深度保洁与收纳整理',
    category: '服务分类 *',
    chooseCategory: '请选择分类',
    method: '服务方式',
    area: '可服务地区 *',
    allLocations: '请选择国家',
    allCities: '请选择城市',
    allDistricts: '请选择区域',
    priceMode: '价格方式',
    price: '价格说明 *',
    pricePlaceholder: '如：¥120/小时、面议',
    availability: '服务时间 *',
    availabilityPlaceholder: '如：周末全天，工作日晚间',
    description: '服务说明 *',
    descriptionPlaceholder: '说明服务内容、准备事项、不可服务范围、取消规则、附加费用等',
    packages: '服务套餐',
    packagePricingRule: '三档套餐规则：Basic = 基础价 1.0x，Standard = 1.5x，Premium = 2.4x。填写这三个名称后，平台会按基础价保存统一价格。',
    packageName: '套餐名称',
    packagePrice: '套餐价格',
    packageDuration: '服务时长',
    packageDescription: '套餐说明',
    contactProtect: '联系方式保护',
    chatOnly: '默认通过平台聊天沟通',
    afterBooking: '确认预约后展示联系方式',
    submitting: '提交中...',
    submit: '提交审核',
    update: '保存并重新审核',
    failed: '提交失败，请稍后再试',
    tipsTitle: '发布建议',
    tips: '服务说明越具体，越容易获得咨询。建议补充服务范围、案例、证书、从业经验、常见问题和取消说明。',
    safetyTitle: '安全审核',
    safety: '健康运动、未成年人相关、上门服务等类目会展示安全提示；医疗、金融投资、法律代理等强监管内容不建议首期开放。',
    steps: ['填写资料', '提交审核', '上线展示'],
  } : {
    kicker: 'Publish service',
    title: 'Create a service people can book',
    subtitle: 'Describe the service, pricing, location, and delivery window so buyers can browse, ask, and order directly.',
    success: 'Submitted for review',
    editSuccess: 'Service updated and resubmitted for review',
    successText: 'High-risk categories may require manual or qualification review. Standard services will appear after approval.',
    back: 'Back to services',
    serviceTitle: 'Service title *',
    titlePlaceholder: 'Example: Home deep cleaning and closet organization',
    category: 'Service category *',
    chooseCategory: 'Select a category',
    method: 'Service type',
    area: 'Service area *',
    allLocations: 'Select a country',
    allCities: 'Select a city',
    allDistricts: 'Select a district',
    priceMode: 'Price mode',
    price: 'Price note *',
    pricePlaceholder: 'Example: ¥120/hour, negotiable',
    availability: 'Availability *',
    availabilityPlaceholder: 'Example: weekends all day, weekday evenings',
    description: 'Service description *',
    descriptionPlaceholder: 'Describe scope, preparation, exclusions, cancellation rules, and extra fees',
    packages: 'Service packages',
    packagePricingRule: 'Three-tier rule: Basic = 1.0x base price, Standard = 1.5x, Premium = 2.4x. The platform normalizes these prices when the names match.',
    packageName: 'Package name',
    packagePrice: 'Package price',
    packageDuration: 'Duration',
    packageDescription: 'Package description',
    contactProtect: 'Contact protection',
    chatOnly: 'Use platform chat by default',
    afterBooking: 'Show contact after booking confirmation',
    submitting: 'Submitting...',
    submit: 'Submit for review',
    update: 'Save and resubmit',
    failed: 'Submission failed. Please try again later.',
    tipsTitle: 'Publishing tips',
    tips: 'Specific service details make inquiries more likely. Add scope, cases, certificates, experience, FAQs, and cancellation rules.',
    safetyTitle: 'Safety review',
    safety: 'Health, minors-related, and on-site services may show safety prompts. Highly regulated medical, financial, and legal services are not recommended for the first release.',
    steps: ['Fill profile', 'Submit review', 'Go live'],
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '', category: '', method: '上门', area: '', priceMode: '按次',
    price: '', availability: '', description: '', contactProtect: '平台聊天',
  });
  const [packages, setPackages] = useState([
    { name: '', price: '', duration: '', description: '' },
    { name: '', price: '', duration: '', description: '' },
    { name: '', price: '', duration: '', description: '' },
  ]);
  const [locationParts, setLocationParts] = useState(['', '', '']);

  useEffect(() => {
    if (editId) return;
    const next = [user?.country || '', user?.city || '', user?.district || ''];
    setLocationParts(next);
    setForm(current => ({ ...current, area: next.filter(Boolean).join(':') }));
  }, [editId, user?.country, user?.city, user?.district]);

  useEffect(() => {
    if (!editId) return;
    let active = true;
    setEditing(true);
    api.get(`/marketplace/services/${editId}`)
      .then(res => {
        if (!active) return;
        const service = res.data.service || res.data;
        setForm({
          title: service.title || '',
          category: service.category || '',
          method: service.method || '上门',
          area: service.area || '',
          priceMode: service.priceMode || '',
          price: service.price || '',
          availability: service.availability || '',
          description: service.description || '',
          contactProtect: service.contactProtect || '平台聊天',
        });
        setLocationParts(splitLocation(service.area));
        const nextPackages = Array.isArray(service.packages) ? service.packages : [];
        setPackages([
          ...nextPackages.slice(0, 3),
          ...Array.from({ length: Math.max(0, 3 - nextPackages.length) }, () => ({ name: '', price: '', duration: '', description: '' })),
        ].slice(0, 3));
      })
      .catch(err => {
        if (active) setError(err.response?.data?.error || text.failed);
      });
    return () => { active = false; };
  }, [editId, text.failed]);

  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));
  const setPackageField = (index, key) => e => {
    const value = e.target.value;
    setPackages(rows => rows.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [key]: value } : row
    )));
  };

  const updateLocationPart = (index, value) => {
    setLocationParts(current => {
      const next = [...current];
      next[index] = value;
      if (index === 0) {
        next[1] = '';
        next[2] = '';
      } else if (index === 1) {
        next[2] = '';
      }
      setForm(currentForm => ({ ...currentForm, area: next.filter(Boolean).join(':') }));
      return next;
    });
  };

  const selectedCountry = LOCATION_TREE.find(item => item.value === locationParts[0]);
  const selectedCity = selectedCountry?.cities.find(item => item.value === locationParts[1]);
  const districtRequired = Boolean(selectedCity?.districts?.length);

  const submit = async e => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
          if (editId) {
            await api.put(`/marketplace/services/${editId}`, { ...form, packages });
          } else {
            await api.post('/marketplace/services', { ...form, packages });
          }
          setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || text.failed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="market-page post-flow-page">
      <div className="container publish-wrap">
        <header className="post-flow-hero">
          <div>
            <div className="market-kicker">{text.kicker}</div>
            <h1 className="detail-title">{text.title}</h1>
            <p>{text.subtitle}</p>
          </div>
          <div className="post-flow-steps" aria-label={text.title}>
            {text.steps.map((step, index) => (
              <span key={step}><b>{index + 1}</b>{step}</span>
            ))}
          </div>
        </header>
        <div className="publish-layout post-flow-layout">
          <div className="detail-card post-flow-card">
            {submitted ? (
              <div>
                    <h2>{editId ? text.editSuccess : text.success}</h2>
                <p className="card-muted" style={{ margin: '10px 0 18px' }}>{text.successText}</p>
                <button className="btn btn-primary" onClick={() => navigate('/services')}>{text.back}</button>
              </div>
            ) : (
              <form onSubmit={submit}>
                {error && <div className="form-error">{error}</div>}
                <div className="form-group">
                  <label>{text.serviceTitle}</label>
                  <input required value={form.title} onChange={set('title')} placeholder={text.titlePlaceholder} />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label>{text.category}</label>
                    <select required value={form.category} onChange={set('category')}>
                      <option value="">{text.chooseCategory}</option>
                      {serviceCategories.map(c => <option key={c.name} value={c.name}>{localizeCategory(c.name)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{text.method}</label>
                    <select value={form.method} onChange={set('method')}>
                      {serviceMethods.map(m => <option key={m} value={m}>{localizeMethod(m)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label>{text.area}</label>
                    <div className="publish-location-grid">
                      <select required value={locationParts[0]} onChange={event => updateLocationPart(0, event.target.value)}>
                        <option value="">{text.allLocations}</option>
                        {LOCATION_TREE.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                      </select>
                      <select required value={locationParts[1]} disabled={!selectedCountry} onChange={event => updateLocationPart(1, event.target.value)}>
                        <option value="">{text.allCities}</option>
                        {selectedCountry?.cities.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                      </select>
                      <select required={districtRequired} value={locationParts[2]} disabled={!selectedCity || !districtRequired} onChange={event => updateLocationPart(2, event.target.value)}>
                        <option value="">{text.allDistricts}</option>
                        {selectedCity?.districts.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{text.priceMode}</label>
                    <select value={form.priceMode} onChange={set('priceMode')}>
                      {priceModes.map(m => <option key={m} value={m}>{translatePhrase(m)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label>{text.price}</label>
                    <input required value={form.price} onChange={set('price')} placeholder={text.pricePlaceholder} />
                  </div>
                  <div className="form-group">
                    <label>{text.availability}</label>
                    <input required value={form.availability} onChange={set('availability')} placeholder={text.availabilityPlaceholder} />
                  </div>
                </div>
                <div className="form-group">
                  <label>{text.description}</label>
                  <textarea required value={form.description} onChange={set('description')} placeholder={text.descriptionPlaceholder} />
                </div>
                <div className="detail-section package-editor-section">
                  <h2>{text.packages}</h2>
                  <p className="form-note package-pricing-rule">{text.packagePricingRule}</p>
                  <div className="package-editor-list">
                    {packages.map((item, index) => (
                      <div className="package-editor-row" key={index}>
                        <div className="grid-2">
                          <div className="form-group">
                            <label>{text.packageName}</label>
                            <input value={item.name} onChange={setPackageField(index, 'name')} />
                          </div>
                          <div className="form-group">
                            <label>{text.packagePrice}</label>
                            <input value={item.price} onChange={setPackageField(index, 'price')} />
                          </div>
                        </div>
                        <div className="grid-2">
                          <div className="form-group">
                            <label>{text.packageDuration}</label>
                            <input value={item.duration} onChange={setPackageField(index, 'duration')} />
                          </div>
                          <div className="form-group">
                            <label>{text.packageDescription}</label>
                            <input value={item.description} onChange={setPackageField(index, 'description')} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>{text.contactProtect}</label>
                  <select value={form.contactProtect} onChange={set('contactProtect')}>
                    <option value="平台聊天">{text.chatOnly}</option>
                    <option value="预约后展示">{text.afterBooking}</option>
                  </select>
                </div>
                    <button className="btn btn-primary" type="submit" disabled={submitting}>{submitting ? text.submitting : (editing ? text.update : text.submit)}</button>
              </form>
            )}
          </div>
          <aside className="detail-card post-flow-side">
            <h2>{text.tipsTitle}</h2>
            <p className="form-note">{text.tips}</p>
            <div className="detail-section">
              <h2>{text.safetyTitle}</h2>
              <p className="form-note">{text.safety}</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
